import {
  Button,
  FormErrorSummary,
  Input,
  Modal,
  SegmentedControl,
  formatCivilDate,
  formatCurrency,
} from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Pagination, RequestAlert } from '../../app/data-view.js';
import {
  statementImportsApi,
  getApiError,
  type FinanceAccount,
  type StatementImportSummary,
} from '../../lib/api.js';
import { formatDateTime, getBrowserTimeZone } from '../../lib/time-zone.js';

export function StatementImportHistory({
  account,
  onClose,
  onChanged,
  canUndo = true,
  canRestore = false,
  timeZone = getBrowserTimeZone(),
}: {
  account: FinanceAccount;
  onClose: () => void;
  onChanged: (count: number, restored: boolean) => Promise<void>;
  canUndo?: boolean;
  canRestore?: boolean;
  timeZone?: string;
}) {
  const top = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<StatementImportSummary>();
  const [section, setSection] = useState<'movements' | 'events'>('movements');
  const [detailSearch, setDetailSearch] = useState('');
  const [detailOffset, setDetailOffset] = useState(0);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const restore = !!selected && selected.activeRows === 0 && selected.withdrawnRows > 0;
  const actionable = !!selected && (restore ? canRestore : canUndo && selected.activeRows > 0);
  const actionLabel = restore ? 'Restaurar importação' : 'Desfazer importação';
  const page = useQuery({
    queryKey: ['company', 'finance', 'imports', account.id, search.trim(), offset],
    queryFn: () => statementImportsApi.list(account.id, search.trim(), offset),
    retry: false,
  });
  const details = useQuery({
    queryKey: [
      'company',
      'finance',
      'import-history',
      selected?.id,
      section,
      detailSearch.trim(),
      detailOffset,
    ],
    queryFn: () =>
      statementImportsApi.history(selected!.id, section, detailSearch.trim(), detailOffset),
    enabled: !!selected,
    retry: false,
  });
  async function submit() {
    if (!selected || !actionable) return;
    setError(undefined);
    if (reason.trim().length < 3) {
      setReasonError('Informe um motivo com pelo menos 3 caracteres.');
      return;
    }
    setReasonError(undefined);
    setSaving(true);
    try {
      const result = await (restore ? statementImportsApi.restore : statementImportsApi.undo)(
        selected.id,
        reason.trim(),
      );
      await onChanged(result.changedRows, restore);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      title={selected ? 'Histórico da importação' : 'Importações do extrato'}
      description={`Conta financeira: ${account.displayName}. O histórico inclui todos os períodos.`}
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
      footer={
        <div className="flex w-full flex-col gap-3">
          {error ? <RequestAlert message={error} /> : null}
          <div className="flex flex-wrap justify-end gap-2">
            {selected ? (
              <>
                {actionable ? <FormErrorSummary form="undo-statement-import" /> : null}
                <Button
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    setSelected(undefined);
                    setError(undefined);
                  }}
                >
                  Voltar
                </Button>
                {actionable ? (
                  <Button
                    type="submit"
                    form="undo-statement-import"
                    loading={saving}
                    disabled={selected.blockers.length > 0}
                  >
                    {actionLabel}
                  </Button>
                ) : null}
              </>
            ) : (
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div ref={top} />
      {selected ? (
        <div className="space-y-4">
          <p className="break-words font-semibold">{selected.originalFileName}</p>
          <p>
            {selected.activeRows} ativos · {selected.withdrawnRows} retirados ·{' '}
            {selected.importedRows} importados
          </p>
          {actionable ? (
            <form
              id="undo-statement-import"
              className="space-y-4"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <p>
                {restore
                  ? `${selected.withdrawnRows} movimento(s) voltarão ao extrato e ao saldo com os mesmos registros. Cancelamentos individuais permanecem preservados.`
                  : `${selected.activeRows} movimento(s) serão retirados do extrato, do saldo e da conciliação. Os registros continuam neste histórico e a importação poderá ser restaurada.`}
              </p>
              <div className="rounded-xl bg-surface-elevated p-3 text-sm">
                <p>
                  Entradas:{' '}
                  <strong>{formatCurrency(selected.inflowCents, selected.currency)}</strong>
                </p>
                <p>
                  Saídas:{' '}
                  <strong>{formatCurrency(selected.outflowCents, selected.currency)}</strong>
                </p>
              </div>
              <p className="text-sm text-content-muted">
                O arquivo e os movimentos continuam protegidos contra reimportação nesta mesma conta
                financeira. Para corrigir o destino, importe na conta financeira correta. A
                restauração verifica duplicidades e meses fechados.
              </p>
              {selected.blockers.map((blocker) => (
                <p key={blocker} role="alert" className="text-sm text-warning">
                  {blocker}
                </p>
              ))}
              <Input
                label={restore ? 'Motivo da restauração' : 'Motivo do desfazimento'}
                value={reason}
                maxLength={240}
                error={reasonError}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonError(undefined);
                }}
              />
            </form>
          ) : null}
          <SegmentedControl
            label="Conteúdo do histórico"
            tabs
            value={section}
            options={[
              { value: 'movements', label: 'Movimentos' },
              { value: 'events', label: 'Auditoria' },
            ]}
            onChange={(value) => {
              setSection(value);
              setDetailOffset(0);
              setDetailSearch('');
            }}
          />
          <Input
            label={section === 'events' ? 'Buscar na auditoria' : 'Buscar movimento no histórico'}
            value={detailSearch}
            onChange={(event) => {
              setDetailSearch(event.target.value);
              setDetailOffset(0);
            }}
          />
          <p className="text-sm text-content-muted">
            {section === 'events'
              ? 'Eventos mais recentes primeiro. Cada evento identifica o movimento afetado.'
              : 'Movimentos em ordem de data, incluindo retirados e cancelados.'}
          </p>
          {details.isLoading ? (
            <p>Carregando histórico…</p>
          ) : details.error ? (
            <RequestAlert message={getApiError(details.error)} />
          ) : (
            <>
              {details.data?.total === 0 ? <p>Nenhum registro encontrado.</p> : null}
              <ul className="space-y-3">
                {details.data?.movements.map((row) => (
                  <li
                    key={row.id}
                    className="break-words rounded-xl border border-surface-border p-3 text-sm"
                  >
                    <p className="font-semibold">{row.description}</p>
                    <p>
                      {formatCivilDate(row.occurredOn)} ·{' '}
                      {row.direction === 'INFLOW' ? 'Entrada' : 'Saída'} ·{' '}
                      {formatCurrency(row.amountCents, selected.currency)}
                    </p>
                    <p>
                      {row.status === 'WITHDRAWN'
                        ? 'Retirado pela importação'
                        : row.status === 'CANCELED'
                          ? 'Cancelado individualmente'
                          : 'Ativo'}
                    </p>
                    {row.reason ? <p>Motivo: {row.reason}</p> : null}
                    <p className="text-content-muted">Referência do movimento: {row.id}</p>
                  </li>
                ))}
                {details.data?.events.map((event) => (
                  <li
                    key={event.id}
                    className="break-words rounded-xl border border-surface-border p-3 text-sm"
                  >
                    <p className="font-semibold">{event.summary}</p>
                    <p>
                      {event.actor} · {formatDateTime(event.createdAt, timeZone)}
                    </p>
                    {event.reason ? <p>Motivo: {event.reason}</p> : null}
                    <p className="text-content-muted">
                      Referência do movimento: {event.movementId}
                    </p>
                  </li>
                ))}
              </ul>
              {(details.data?.total ?? 0) > 20 ? (
                <Pagination
                  total={details.data!.total}
                  limit={20}
                  offset={detailOffset}
                  onOffsetChange={setDetailOffset}
                  itemLabel={(n) => (n === 1 ? 'registro' : 'registros')}
                />
              ) : null}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <Input
            label="Buscar importação"
            placeholder="Nome do arquivo"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
          {page.isLoading ? (
            <p>Carregando importações…</p>
          ) : page.error ? (
            <RequestAlert message={getApiError(page.error)} />
          ) : (
            <>
              <p className="text-sm text-content-muted">
                {page.data?.total ?? 0} importações · mais recentes primeiro
              </p>
              {page.data?.imports.length === 0 ? (
                <p>Nenhuma importação encontrada.</p>
              ) : (
                <ul className="space-y-3">
                  {page.data?.imports.map((batch) => (
                    <li key={batch.id} className="rounded-xl border border-surface-border p-3">
                      <p className="break-words font-semibold">{batch.originalFileName}</p>
                      <p className="text-sm text-content-muted">
                        {batch.importedRows} movimentos importados · {batch.activeRows} ativos ·{' '}
                        {batch.withdrawnRows} retirados
                      </p>
                      {batch.from && batch.to ? (
                        <p className="text-sm">
                          {formatCivilDate(batch.from)} a {formatCivilDate(batch.to)}
                        </p>
                      ) : null}
                      <Button
                        className="mt-2"
                        variant="outline"
                        onClick={() => {
                          setSelected(batch);
                          setReason('');
                          setReasonError(undefined);
                          setSection('movements');
                          setDetailSearch('');
                          setDetailOffset(0);
                        }}
                      >
                        Conferir importação
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              {(page.data?.total ?? 0) > 10 ? (
                <Pagination
                  total={page.data!.total}
                  limit={10}
                  offset={offset}
                  onOffsetChange={setOffset}
                  itemLabel={(n) => (n === 1 ? 'importação' : 'importações')}
                />
              ) : null}
            </>
          )}
        </div>
      )}
      <Button
        variant="ghost"
        className="mt-4"
        onClick={() =>
          top.current?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'instant'
              : 'smooth',
            block: 'start',
          })
        }
      >
        Voltar ao topo
      </Button>
    </Modal>
  );
}
